import { MoldOrder, MoldOrderFirstLevelType, MoldOrderStatus, MoldOrderType, StateInstance } from '../../types/mold';
import { formatShortThaiDate } from '../../utils/dateUtils';

export const getDuration = (moldOrder: MoldOrder) => {
    const startTime = new Date(moldOrder.createdAt);
    const endTime = new Date(moldOrder.updatedAt);
    const duration = Math.abs(endTime.getTime() - startTime.getTime());
    return Math.floor(duration / (1000 * 60 * 60)); // Convert to hours
}

/**
 * Date the order entered its current status. Reverse-find handles rework loops
 * where a status recurs — we want the most recent entry into it.
 */
export const getCurrentStatusStartDate = (moldOrder: MoldOrder): Date | null => {
    const current = [...(moldOrder.stateInstances ?? [])].reverse()
        .find(stateInstance => stateInstance.status === moldOrder.status.toString());
    return current?.startTime ? new Date(current.startTime) : null;
}

// getSupervisorReviewDate / getReporterConfirmationDate / getOperationDate /
// getOperationDuration prefer the server-computed `derived` block (backend
// internal/server/derivedfields — the same values the CSV export emits, so screen and export
// agree), and fall back to the legacy client-side derivation for payloads without it.
export const getSupervisorReviewDate = (moldOrder: MoldOrder): Date | null => {
    if (moldOrder.derived?.supervisorReviewDate) {
        return new Date(moldOrder.derived.supervisorReviewDate);
    }
    const supervisorReviewStep = moldOrder.stateInstances.find(stateInstance => stateInstance.status === MoldOrderStatus.SUPERVISOR_REVIEW.toString());
    // endTime guard: an open review step has no endTime yet — no date beats Invalid Date.
    if (supervisorReviewStep?.endTime) {
        return new Date(supervisorReviewStep.endTime);
    }

    return null;
}

export const getSupervisorReviewDateString = (moldOrder: MoldOrder): string => {
    const completedDate = getSupervisorReviewDate(moldOrder);
    return completedDate
        ? formatShortThaiDate(completedDate)
        : '-'
}

export const getReporterConfirmationDate = (moldOrder: MoldOrder): Date | null => {
    if (moldOrder.derived?.reporterConfirmationDate) {
        return new Date(moldOrder.derived.reporterConfirmationDate);
    }
    const reporterConfirmationDate = moldOrder.stateInstances.find(stateInstance => stateInstance.status === MoldOrderStatus.REPORTER_CONFIRM.toString());
    if (reporterConfirmationDate?.endTime) {
        return new Date(reporterConfirmationDate.endTime);
    }

    return null;
}

export const getReporterConfirmationDateString = (moldOrder: MoldOrder): string => {
    const reporterConfirmationDate = getReporterConfirmationDate(moldOrder);
    return reporterConfirmationDate
        ? formatShortThaiDate(reporterConfirmationDate)
        : '-'
}

export const getOperationDate = (moldOrder: MoldOrder): Date | null => {
    if (moldOrder.derived?.operationDate) {
        return new Date(moldOrder.derived.operationDate);
    }
    const inProgressStep = moldOrder.stateInstances.find(stateInstance => stateInstance.status === MoldOrderStatus.IN_PROGRESS.toString());
    if (inProgressStep?.startTime) {
        return new Date(inProgressStep.startTime);
    }

    return null;
}

export const getOperationDateString = (moldOrder: MoldOrder): string => {
    const operationDate = getOperationDate(moldOrder);
    return operationDate
        ? formatShortThaiDate(operationDate)
        : '-'
}

/**
 * Duration of the operation step in WHOLE MINUTES (matching the column label
 * "เวลาที่ใช้ล้าง(นาที)"). Two deliberate fixes vs the legacy derivation, matching the
 * server's derivedfields.Compute: it measured start -> order.updatedAt (drifts whenever the
 * order is touched later) and rendered raw milliseconds under the minutes label. Now: derived
 * minutes from the server when present, else in_progress startTime -> endTime; null while the
 * step is still open (no fabricated running total).
 */
export const getOperationDuration = (moldOrder: MoldOrder): number | null => {
    if (moldOrder.derived?.operationDurationMinutes != null) {
        return moldOrder.derived.operationDurationMinutes;
    }
    const inProgressStep = moldOrder.stateInstances.find(stateInstance => stateInstance.status === MoldOrderStatus.IN_PROGRESS.toString());
    if (inProgressStep?.startTime && inProgressStep?.endTime) {
        const startTime = new Date(inProgressStep.startTime);
        const endTime = new Date(inProgressStep.endTime);
        const durationMs = endTime.getTime() - startTime.getTime();
        if (!Number.isFinite(durationMs) || durationMs < 0) {
            return null;
        }
        return Math.floor(durationMs / (1000 * 60));
    }
    return null;
}

export const getOperationDurationString = (moldOrder: MoldOrder): string | null => {
    const duration = getOperationDuration(moldOrder);
    return duration != null ? `${duration}` : null;
}

export const getAssignedTechnician = (moldOrder: MoldOrder): string | null => {
    const inProgressStep = moldOrder.stateInstances.find(stateInstance => stateInstance.status === MoldOrderStatus.IN_PROGRESS.toString());
    if (inProgressStep) {
        return inProgressStep.assignedTo.fullname || inProgressStep.assignedTo.username;
    }
    return null;
}

export const getApprovalTechnician = (moldOrder: MoldOrder): string | null => {
    const approvalStep = moldOrder.stateInstances.find(stateInstance => stateInstance.status === MoldOrderStatus.SUPERVISOR_REVIEW.toString());
    if (approvalStep) {
        return approvalStep.assignedTo.fullname || approvalStep.assignedTo.username;
    }
    return null;
}

export const getReporterConfirmLabel = (moldOrder: MoldOrder): string | null => {
    const approvalStep = moldOrder.stateInstances.find(stateInstance => stateInstance.status === MoldOrderStatus.REPORTER_CONFIRM.toString());
    if (approvalStep) {
        return approvalStep.assignedTo.fullname || approvalStep.assignedTo.username;
    }
    return null;
}

export const getMachineNumber = (moldOrder: MoldOrder): string | null => {
    return moldOrder?.orderDetail?.machineToProduce ?? null;
}

/**
 * The state instance where the reject action happened, for orders that ended
 * rejected. The reject comment is saved onto the then-current state instance
 * right before the transition, so the decision data lives on the last
 * non-terminal instance — the terminal 'rejected' instance carries none.
 */
export const getRejectingStateInstance = (moldOrder: MoldOrder): StateInstance | null => {
    if (moldOrder.status !== MoldOrderStatus.END_REJECTED) return null;
    const nonTerminal = (moldOrder.stateInstances ?? [])
        .filter(stateInstance => stateInstance.status !== MoldOrderStatus.END_REJECTED.toString());
    return nonTerminal.length > 0 ? nonTerminal[nonTerminal.length - 1] : null;
}

export const getRejectedReason = (moldOrder: MoldOrder): string | null => {
    const notes = getRejectingStateInstance(moldOrder)?.notes?.trim();
    return notes ? notes : null;
}

export const getRejectedPerson = (moldOrder: MoldOrder): string | null => {
    const rejectingStep = getRejectingStateInstance(moldOrder);
    if (!rejectingStep) return null;
    return rejectingStep.assignedTo?.fullname || rejectingStep.assignedTo?.username || null;
}

/**
 * Display label of the workflow state the rejection happened in, using the
 * same getStatusText mapping the status column shows. Falls back to the raw
 * status when no labeler is provided.
 */
export const getRejectedState = (
    moldOrder: MoldOrder,
    getStatusText?: (status: MoldOrderStatus, type?: MoldOrderType) => string,
): string | null => {
    const rejectingStep = getRejectingStateInstance(moldOrder);
    if (!rejectingStep) return null;
    return getStatusText?.(rejectingStep.status, moldOrder.type) ?? rejectingStep.status;
}

export const getRejectPhotoTag = (moldOrder: MoldOrder): string => {
    return moldOrder.orderDetail?.rejectPhotoTag ?? `${moldOrder.status.toLowerCase()}-reject`;
}

export const getDraftRejectPhotoTag = (moldOrder: MoldOrder): string => {
    return `${moldOrder.status.toLowerCase()}-reject`;
}

export const getFinalRejectPhotoTag = (moldOrder: MoldOrder): string => {
    return moldOrder.orderDetail?.rejectPhotoTag ?? `${moldOrder.status.toLowerCase()}-reject-final`;
}

export const getMoldOrderTypeLabel = (type: MoldOrderType): string => {
    if (type.startsWith('maintenance')) {
        return 'ล้างแม่พิมพ์';
    } else if (type.startsWith('repair')) {
        return 'ซ่อมแม่พิมพ์';
    }
    else if (type.startsWith('mold_acceptance')) {
        return 'รับเข้าแม่พิมพ์';
    }
    return 'ไม่ทราบประเภท';
}

export const isMaintenanceOrder = (moldOrder: MoldOrder): boolean => {
    return moldOrder.type.startsWith('maintenance');
}

export const isRepairOrder = (moldOrder: MoldOrder): boolean => {
    return isRepairOrderType(moldOrder.type);
}

export const isRepairOrderType = (orderType: MoldOrderType): boolean => {
    return orderType.startsWith('repair') || orderType === MoldOrderType.MODIFY;
}

export const isMoldAcceptanceOrder = (moldOrder: MoldOrder): boolean => {
    return moldOrder.type.startsWith('mold_acceptance');
}

export const getArrivalDate = (moldOrder: MoldOrder): Date | null => {
    if (moldOrder.orderDetail?.form?.moldDate?.arrivalDate) {
        return new Date(moldOrder.orderDetail.form.moldDate.arrivalDate);
    }
    const arrivalStep = moldOrder.stateInstances.find(stateInstance => stateInstance.status === MoldOrderStatus.PENDING_ARRIVAL.toString());
    if (arrivalStep && arrivalStep.endTime) {
        return new Date(arrivalStep.endTime);
    }
    return null;
}
